import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { PinPad } from '@/components/ui/PinPad';
import { toast } from '@/components/ui/Toast';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/**
 * Sets (or changes) the PIN of this device session: typed twice. After 10 minutes without touches
 * the device asks for it (lock screen).
 */
export function PinDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { setPin, me } = useAuth();
  const [first, setFirst] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFirst(null);
    setValue('');
    setError(null);
  };

  const submit = async (pin: string) => {
    if (first == null) {
      setFirst(pin);
      setValue('');
      setError(null);
      return;
    }
    if (pin !== first) {
      setError('I due PIN non coincidono: ripeti dall’inizio');
      setFirst(null);
      setValue('');
      return;
    }
    setBusy(true);
    try {
      await setPin(pin);
      toast({
        title: 'PIN impostato',
        description: 'Dopo 10 minuti di inattività il dispositivo chiederà il PIN.',
        variant: 'success',
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
      setFirst(null);
      setValue('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{me?.hasPin ? 'Cambia PIN' : 'Imposta PIN'}</DialogTitle>
          <DialogDescription>
            {first == null
              ? 'Scegli un PIN di 4–6 cifre per sbloccare questo dispositivo.'
              : 'Digita di nuovo lo stesso PIN per confermare.'}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
        <PinPad
          value={value}
          onChange={(v) => {
            setValue(v);
            if (error) setError(null);
          }}
          onSubmit={submit}
          disabled={busy}
          label={first == null ? 'Nuovo PIN' : 'Conferma PIN'}
          captureKeyboard={open}
        />
      </DialogContent>
    </Dialog>
  );
}
